import React, { useState, useEffect } from "react";
import axios from "axios";
import Bugsnag from "@bugsnag/js";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Alert,
  Spinner,
  Progress,
} from "reactstrap";
import constants from "../../Constants";

const baseURL = constants.baseURL;

// Star component for rating
const Star = ({ filled, hovered, onClick, onMouseEnter, onMouseLeave, size = 48 }) => (
  <span
    onClick={onClick}
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
    style={{
      cursor: "pointer",
      fontSize: `${size}px`,
      color: filled || hovered ? "#FFD700" : "#E0E0E0",
      transition: "all 0.2s ease",
      display: "inline-block",
      transform: hovered ? "scale(1.2)" : "scale(1)",
      marginRight: "4px",
      textShadow: filled || hovered ? "0 0 15px rgba(255, 215, 0, 0.6)" : "none",
    }}
  >
    ★
  </span>
);

// Display only stars (no interaction)
const StarDisplay = ({ rating, size = 24 }) => (
  <span>
    {[1, 2, 3, 4, 5].map((star) => (
      <span
        key={star}
        style={{
          fontSize: `${size}px`,
          color: star <= rating ? "#FFD700" : "#E0E0E0",
          marginRight: "2px",
        }}
      >
        ★
      </span>
    ))}
  </span>
);

// Helper function to get rating label
const getRatingLabel = (rating) => {
  const labels = {
    1: "Poor 😞",
    2: "Fair 😐",
    3: "Good 🙂",
    4: "Very Good 😊",
    5: "Excellent! 🌟",
  };
  return labels[rating] || "Tap to rate";
};

// Step indicator component
const StepIndicator = ({ steps, currentStep, completedSteps }) => (
  <div style={styles.stepIndicator}>
    {steps.map((step, index) => {
      const isCompleted = completedSteps.includes(index);
      const isCurrent = currentStep === index;
      return (
        <div key={index} style={styles.stepItem}>
          <div
            style={{
              ...styles.stepCircle,
              ...(isCompleted ? styles.stepCompleted : {}),
              ...(isCurrent ? styles.stepActive : {}),
            }}
          >
            {isCompleted ? "✓" : index + 1}
          </div>
          <span
            style={{
              ...styles.stepLabel,
              ...(isCurrent ? styles.stepLabelActive : {}),
            }}
          >
            {step.label}
          </span>
          {index < steps.length - 1 && <div style={styles.stepLine} />}
        </div>
      );
    })}
  </div>
);

// Rating Step Component
const RatingStep = ({
  title,
  subtitle,
  icon,
  rating,
  setRating,
  hoveredRating,
  setHoveredRating,
}) => (
  <div style={styles.stepContent}>
    <div style={styles.stepIcon}>{icon}</div>
    <h3 style={styles.stepTitle}>{title}</h3>
    <p style={styles.stepSubtitle}>{subtitle}</p>
    
    <div style={styles.starsContainer}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          filled={star <= rating}
          hovered={star <= hoveredRating}
          onClick={() => setRating(star)}
          onMouseEnter={() => setHoveredRating(star)}
          onMouseLeave={() => setHoveredRating(0)}
          size={48}
        />
      ))}
    </div>
    
    <p style={styles.ratingFeedback}>
      {rating > 0 ? getRatingLabel(rating) : "Tap a star to rate"}
    </p>
  </div>
);

const RateDriverSection = () => {
  const [orderData, setOrderData] = useState(null);
  const [driverData, setDriverData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [authToken, setAuthToken] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  
  // Current step in the wizard
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  
  // Driver ratings
  const [driverRating, setDriverRating] = useState(0);
  const [driverHovered, setDriverHovered] = useState(0);
  
  // Vehicle rating
  const [vehicleRating, setVehicleRating] = useState(0);
  const [vehicleHovered, setVehicleHovered] = useState(0);
  
  // Handling rating
  const [handlingRating, setHandlingRating] = useState(0);
  const [handlingHovered, setHandlingHovered] = useState(0);
  
  // Admin/Service rating
  const [serviceRating, setServiceRating] = useState(0);
  const [serviceHovered, setServiceHovered] = useState(0);
  
  // Comment
  const [comment, setComment] = useState("");
  
  // Existing ratings
  const [existingDriverRating, setExistingDriverRating] = useState(null);
  const [existingAdminRating, setExistingAdminRating] = useState(null);
  
  // Success state
  const [allComplete, setAllComplete] = useState(false);

  // Get URL params
  const urlParams = new URLSearchParams(window.location.search);
  const orderid = urlParams.get("orderid");
  const email = urlParams.get("email");
  const ratingType = urlParams.get("type") || "both";

  const showDriver = ratingType === "driver" || ratingType === "both";
  const showAdmin = ratingType === "admin" || ratingType === "both";

  // Define steps based on what needs to be rated
  const getSteps = () => {
    const steps = [];
    
    // Show driver step only if not already rated
    if (showDriver && driverData && !existingDriverRating) {
      steps.push({ key: "driver", label: "Driver", icon: "👤" });
    }
    
    // Always show vehicle and handling steps when driver flow is shown
    // (even if driver already rated - user can still rate vehicle/handling)
    if (showDriver && driverData) {
      steps.push({ key: "vehicle", label: "Vehicle", icon: "🚛" });
      steps.push({ key: "handling", label: "Handling", icon: "📦" });
    }
    
    // Always show service/admin rating step when showAdmin is true
    if (showAdmin) {
      steps.push({ key: "service", label: "Service", icon: "📋" });
    }
    
    steps.push({ key: "comment", label: "Feedback", icon: "💬" });
    
    return steps;
  };

  const steps = getSteps();

  useEffect(() => {
    const fetchOrderAndDriver = async () => {
      if (!orderid) {
        setError("No order ID provided");
        setLoading(false);
        return;
      }

      if (!email) {
        setError("Email is required for verification");
        setLoading(false);
        return;
      }

      try {
        const orderResponse = await axios.post(`${baseURL}/track-order`, {
          orderid,
          email,
        });

        if (!orderResponse.data || !orderResponse.data.orderid) {
          setError("Order not found or email does not match");
          setLoading(false);
          return;
        }

        const order = orderResponse.data;
        setOrderData(order);

        if (order.token) {
          setAuthToken(order.token);
        }

        if (!["finished", "completed"].includes(order.orderstatus)) {
          setError("This order cannot be rated yet. It must be completed first.");
          setLoading(false);
          return;
        }

        if (order.driverfirstname) {
          setDriverData({
            firstname: order.driverfirstname,
            lastname: order.driverlastname,
          });
        }

        // Check existing ratings
        if (showDriver) {
          try {
            const ratingResponse = await axios.get(`${baseURL}/customer-driver-rating`, {
              params: { orderid },
            });
            if (ratingResponse.data.rated) {
              setExistingDriverRating(ratingResponse.data.rating);
            }
          } catch (ratingErr) {
            console.log("No existing driver rating found");
          }
        }

        if (showAdmin) {
          try {
            const adminRatingResponse = await axios.get(`${baseURL}/customer-admin-rating`, {
              params: { orderid },
            });
            if (adminRatingResponse.data.rated) {
              setExistingAdminRating(adminRatingResponse.data.rating);
            }
          } catch (ratingErr) {
            console.log("No existing admin rating found");
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("Error fetching order:", err);
        Bugsnag.notify(err);
        if (err.response?.status === 404) {
          setError("Order not found or email does not match");
        } else {
          setError("Failed to load order details. Please try again.");
        }
        setLoading(false);
      }
    };

    fetchOrderAndDriver();
  }, [orderid, email, showDriver, showAdmin]);

  // Check if already completely rated
  useEffect(() => {
    const driverDone = !showDriver || existingDriverRating;
    const adminDone = !showAdmin || existingAdminRating;
    
    if (driverDone && adminDone && !loading) {
      setAllComplete(true);
    }
  }, [showDriver, showAdmin, existingDriverRating, existingAdminRating, loading]);

  const getCurrentStepKey = () => steps[currentStep]?.key;

  const getCurrentRating = () => {
    const key = getCurrentStepKey();
    switch (key) {
      case "driver": return driverRating;
      case "vehicle": return vehicleRating;
      case "handling": return handlingRating;
      case "service": return serviceRating;
      default: return 0;
    }
  };

  const canProceed = () => {
    const key = getCurrentStepKey();
    if (key === "comment") return true;
    // Allow proceeding if service already rated
    if (key === "service" && existingAdminRating) return true;
    return getCurrentRating() > 0;
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCompletedSteps([...completedSteps, currentStep]);
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCompletedSteps(completedSteps.filter(s => s !== currentStep - 1));
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError("");

    // Build detailed ratings JSON to append to comment
    const detailedRatings = {
      vehicle_rating: vehicleRating,
      handling_rating: handlingRating,
      timestamp: new Date().toISOString(),
    };

    const commentWithDetails = comment.trim() 
      ? `${comment.trim()} ||RATINGS:${JSON.stringify(detailedRatings)}||`
      : `||RATINGS:${JSON.stringify(detailedRatings)}||`;

    try {
      // Submit driver rating if applicable (new driver rating)
      if (showDriver && driverData && !existingDriverRating && driverRating > 0) {
        await axios.post(
          `${baseURL}/customer-rate-driver`,
          {
            orderid,
            rating: driverRating,
            comment: commentWithDetails,
          },
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
      }

      // Submit service rating if applicable
      // Include vehicle/handling ratings in admin comment if driver was already rated
      const adminCommentWithRatings = existingDriverRating && (vehicleRating > 0 || handlingRating > 0)
        ? `${comment.trim() || ""} ||RATINGS:${JSON.stringify(detailedRatings)}||`.trim()
        : comment.trim() || null;

      if (showAdmin && !existingAdminRating && serviceRating > 0) {
        await axios.post(
          `${baseURL}/customer-rate-admin`,
          {
            orderid,
            rating: serviceRating,
            comment: adminCommentWithRatings,
          },
          {
            headers: { Authorization: `Bearer ${authToken}` },
          }
        );
      }

      setAllComplete(true);
    } catch (err) {
      console.error("Error submitting ratings:", err);
      Bugsnag.notify(err);
      setSubmitError(err.response?.data?.error || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <section style={styles.section}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={5} md={7} sm={11}>
              <Card style={styles.card}>
                <CardBody className="text-center py-5">
                  <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
                  <p className="mt-3 text-muted">Loading your trip details...</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    );
  }

  // Error state
  if (error && !orderData) {
    return (
      <section style={styles.section}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={5} md={7} sm={11}>
              <Card style={styles.card}>
                <CardBody className="text-center py-5">
                  <div style={{ fontSize: "56px", marginBottom: "16px" }}>❌</div>
                  <h4 className="text-danger mb-3">Unable to Load</h4>
                  <p className="text-muted">{error}</p>
                  <Button color="primary" href="/" className="mt-3">
                    Go to Homepage
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    );
  }

  // All complete state
  if (allComplete) {
    return (
      <section style={styles.section}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={5} md={7} sm={11}>
              <Card style={styles.card}>
                <CardBody className="text-center py-5">
                  <div style={styles.successAnimation}>🎉</div>
                  <h3 style={styles.thankYouTitle}>Thank You!</h3>
                  <p style={styles.thankYouSubtitle}>
                    Your feedback has been submitted successfully
                  </p>

                  {/* Ratings Summary */}
                  <div style={styles.summaryContainer}>
                    {(existingDriverRating || driverRating > 0) && (
                      <div style={styles.summaryItem}>
                        <span style={styles.summaryIcon}>👤</span>
                        <span style={styles.summaryLabel}>Driver</span>
                        <StarDisplay rating={existingDriverRating?.rating || driverRating} size={18} />
                      </div>
                    )}
                    {vehicleRating > 0 && (
                      <div style={styles.summaryItem}>
                        <span style={styles.summaryIcon}>🚛</span>
                        <span style={styles.summaryLabel}>Vehicle</span>
                        <StarDisplay rating={vehicleRating} size={18} />
                      </div>
                    )}
                    {handlingRating > 0 && (
                      <div style={styles.summaryItem}>
                        <span style={styles.summaryIcon}>📦</span>
                        <span style={styles.summaryLabel}>Handling</span>
                        <StarDisplay rating={handlingRating} size={18} />
                      </div>
                    )}
                    {(existingAdminRating || serviceRating > 0) && (
                      <div style={styles.summaryItem}>
                        <span style={styles.summaryIcon}>📋</span>
                        <span style={styles.summaryLabel}>Service</span>
                        <StarDisplay rating={existingAdminRating?.rating || serviceRating} size={18} />
                      </div>
                    )}
                  </div>

                  <Alert color="success" className="mt-4">
                    Your feedback helps us maintain quality service!
                  </Alert>

                  <Button color="primary" href="/" className="mt-3" style={styles.primaryButton}>
                    Book Another Trip
                  </Button>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    );
  }

  // Step wizard
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <section style={styles.section}>
      <Container>
        <Row className="justify-content-center">
          <Col lg={5} md={7} sm={11}>
            <Card style={styles.card}>
              <CardBody className="py-4 px-4">
                {/* Header */}
                <div className="text-center mb-3">
                  <img src="https://media.qiktruck.co.za/dark-green-qiktruck-logo.png" alt="QikTruck" style={styles.logo} />
                </div>

                {/* Progress Bar */}
                <Progress
                  value={progress}
                  style={styles.progressBar}
                  barStyle={{ background: "linear-gradient(90deg, #FF6B00, #FFD700)" }}
                />

                {/* Step Indicator */}
                <StepIndicator
                  steps={steps}
                  currentStep={currentStep}
                  completedSteps={completedSteps}
                />

                {submitError && (
                  <Alert color="danger" className="mt-3">
                    {submitError}
                  </Alert>
                )}

                {/* Step Content */}
                {getCurrentStepKey() === "driver" && (
                  <RatingStep
                    title="Rate Your Driver"
                    subtitle={driverData ? `${driverData.firstname} ${driverData.lastname}` : "Your driver"}
                    icon="👤"
                    rating={driverRating}
                    setRating={setDriverRating}
                    hoveredRating={driverHovered}
                    setHoveredRating={setDriverHovered}
                  />
                )}

                {getCurrentStepKey() === "vehicle" && (
                  <RatingStep
                    title="Rate the Vehicle"
                    subtitle={orderData?.trucktype || "Delivery vehicle"}
                    icon="🚛"
                    rating={vehicleRating}
                    setRating={setVehicleRating}
                    hoveredRating={vehicleHovered}
                    setHoveredRating={setVehicleHovered}
                  />
                )}

                {getCurrentStepKey() === "handling" && (
                  <RatingStep
                    title="Goods Handling"
                    subtitle="How were your items handled?"
                    icon="📦"
                    rating={handlingRating}
                    setRating={setHandlingRating}
                    hoveredRating={handlingHovered}
                    setHoveredRating={setHandlingHovered}
                  />
                )}

                {getCurrentStepKey() === "service" && (
                  existingAdminRating ? (
                    <div style={styles.stepContent}>
                      <div style={styles.stepIcon}>📋</div>
                      <h3 style={styles.stepTitle}>QikTruck Service</h3>
                      <p style={styles.stepSubtitle}>You already rated our service</p>
                      <StarDisplay rating={existingAdminRating.rating} size={36} />
                      <p style={styles.ratingFeedback}>Thank you for your feedback!</p>
                    </div>
                  ) : (
                    <RatingStep
                      title="QikTruck Service"
                      subtitle="Booking & Support Experience"
                      icon="📋"
                      rating={serviceRating}
                      setRating={setServiceRating}
                      hoveredRating={serviceHovered}
                      setHoveredRating={setServiceHovered}
                    />
                  )
                )}

                {getCurrentStepKey() === "comment" && (
                  <div style={styles.stepContent}>
                    <div style={styles.stepIcon}>💬</div>
                    <h3 style={styles.stepTitle}>Additional Feedback</h3>
                    <p style={styles.stepSubtitle}>Any other comments? (optional)</p>
                    
                    <Input
                      type="textarea"
                      placeholder="Tell us more about your experience..."
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      maxLength={500}
                      rows={4}
                      style={styles.textarea}
                    />
                    <small style={styles.charCount}>{comment.length}/500</small>

                    {/* Summary Preview */}
                    <div style={styles.previewContainer}>
                      <p style={styles.previewTitle}>Your Ratings</p>
                      <div style={styles.previewGrid}>
                        {/* Show existing or new driver rating */}
                        {(existingDriverRating || driverRating > 0) && (
                          <div style={styles.previewItem}>
                            <span>👤 Driver</span>
                            <span>{existingDriverRating?.rating || driverRating}/5</span>
                          </div>
                        )}
                        {vehicleRating > 0 && (
                          <div style={styles.previewItem}>
                            <span>🚛 Vehicle</span>
                            <span>{vehicleRating}/5</span>
                          </div>
                        )}
                        {handlingRating > 0 && (
                          <div style={styles.previewItem}>
                            <span>📦 Handling</span>
                            <span>{handlingRating}/5</span>
                          </div>
                        )}
                        {/* Show existing or new service/admin rating */}
                        {(existingAdminRating || serviceRating > 0) && (
                          <div style={styles.previewItem}>
                            <span>📋 Service</span>
                            <span>{existingAdminRating?.rating || serviceRating}/5</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Show existing comments if any */}
                      {existingDriverRating?.comment && (
                        <div style={styles.existingCommentPreview}>
                          <span style={{ fontWeight: 500 }}>Previous driver feedback:</span>
                          <p style={{ margin: "4px 0 0", fontStyle: "italic", color: "#666" }}>
                            "{existingDriverRating.comment}"
                          </p>
                        </div>
                      )}
                      {existingAdminRating?.comment && (
                        <div style={styles.existingCommentPreview}>
                          <span style={{ fontWeight: 500 }}>Previous service feedback:</span>
                          <p style={{ margin: "4px 0 0", fontStyle: "italic", color: "#666" }}>
                            "{existingAdminRating.comment}"
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div style={styles.navigation}>
                  {currentStep > 0 && (
                    <Button
                      onClick={handleBack}
                      style={styles.backButton}
                    >
                      ← Back
                    </Button>
                  )}
                  
                  {currentStep < steps.length - 1 ? (
                    <Button
                      onClick={handleNext}
                      disabled={!canProceed()}
                      style={{
                        ...styles.nextButton,
                        ...(currentStep === 0 ? { width: "100%" } : {}),
                        ...(!canProceed() ? styles.nextButtonDisabled : {}),
                      }}
                    >
                      Next →
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      disabled={submitting}
                      style={styles.submitButton}
                    >
                      {submitting ? (
                        <>
                          <Spinner size="sm" className="mr-2" /> Submitting...
                        </>
                      ) : (
                        "Submit Feedback ⭐"
                      )}
                    </Button>
                  )}
                </div>

                <p style={styles.skipText}>
                  <a href="/" style={styles.skipLink}>Skip and go to homepage</a>
                </p>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

const styles = {
  section: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #FF6B00 0%, #FF8533 50%, #FFA366 100%)",
    paddingTop: "30px",
    paddingBottom: "30px",
    display: "flex",
    alignItems: "center",
  },
  card: {
    borderRadius: "20px",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.2)",
    border: "none",
    overflow: "hidden",
  },
  logo: {
    maxWidth: "80px",
    marginBottom: "8px",
  },
  progressBar: {
    height: "6px",
    borderRadius: "3px",
    marginBottom: "20px",
    background: "#f0f0f0",
  },
  stepIndicator: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: "24px",
    flexWrap: "wrap",
    gap: "4px",
  },
  stepItem: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  stepCircle: {
    width: "28px",
    height: "28px",
    borderRadius: "50%",
    background: "#e0e0e0",
    color: "#999",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: "600",
    transition: "all 0.3s ease",
  },
  stepCompleted: {
    background: "#4CAF50",
    color: "#fff",
  },
  stepActive: {
    background: "#FF6B00",
    color: "#fff",
    transform: "scale(1.1)",
    boxShadow: "0 4px 12px rgba(255, 107, 0, 0.4)",
  },
  stepLabel: {
    fontSize: "11px",
    color: "#999",
    marginRight: "8px",
    display: "none",
  },
  stepLabelActive: {
    color: "#FF6B00",
    fontWeight: "600",
    display: "block",
  },
  stepLine: {
    width: "20px",
    height: "2px",
    background: "#e0e0e0",
  },
  stepContent: {
    textAlign: "center",
    padding: "20px 0",
    minHeight: "280px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  stepIcon: {
    fontSize: "56px",
    marginBottom: "16px",
  },
  stepTitle: {
    color: "#333",
    fontWeight: "700",
    fontSize: "22px",
    marginBottom: "8px",
  },
  stepSubtitle: {
    color: "#666",
    fontSize: "15px",
    marginBottom: "24px",
  },
  starsContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "16px",
  },
  ratingFeedback: {
    color: "#FF6B00",
    fontWeight: "600",
    fontSize: "18px",
    minHeight: "28px",
  },
  textarea: {
    borderRadius: "12px",
    border: "2px solid #e0e0e0",
    resize: "none",
    fontSize: "15px",
    padding: "14px",
    width: "100%",
    transition: "border-color 0.3s ease",
  },
  charCount: {
    color: "#999",
    fontSize: "12px",
    textAlign: "right",
    display: "block",
    marginTop: "6px",
  },
  previewContainer: {
    background: "#f8f9fa",
    borderRadius: "12px",
    padding: "16px",
    marginTop: "20px",
    width: "100%",
  },
  previewTitle: {
    color: "#333",
    fontWeight: "600",
    fontSize: "14px",
    marginBottom: "12px",
  },
  previewGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "8px",
  },
  previewItem: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "13px",
    color: "#666",
    padding: "6px 10px",
    background: "#fff",
    borderRadius: "6px",
  },
  existingCommentPreview: {
    marginTop: "12px",
    padding: "10px",
    background: "#fff",
    borderRadius: "6px",
    fontSize: "12px",
    color: "#555",
    borderLeft: "3px solid #FF6B00",
  },
  navigation: {
    display: "flex",
    gap: "12px",
    marginTop: "24px",
  },
  backButton: {
    flex: "0 0 100px",
    borderRadius: "10px",
    padding: "12px 16px",
    fontWeight: "600",
    backgroundColor: "#fff",
    border: "2px solid #e0e0e0",
    color: "#555",
    fontSize: "14px",
  },
  nextButton: {
    flex: 1,
    backgroundColor: "#FF6B00",
    border: "none",
    borderRadius: "10px",
    padding: "14px 20px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#fff",
    boxShadow: "0 4px 15px rgba(255, 107, 0, 0.3)",
    cursor: "pointer",
  },
  nextButtonDisabled: {
    backgroundColor: "#ffb380",
    boxShadow: "none",
    cursor: "not-allowed",
    opacity: 0.7,
  },
  submitButton: {
    flex: 1,
    backgroundColor: "#4CAF50",
    border: "none",
    borderRadius: "10px",
    padding: "14px 20px",
    fontSize: "16px",
    fontWeight: "600",
    color: "#fff",
    boxShadow: "0 4px 15px rgba(76, 175, 80, 0.3)",
    cursor: "pointer",
  },
  primaryButton: {
    background: "linear-gradient(135deg, #FF6B00 0%, #FF8533 100%)",
    border: "none",
    borderRadius: "10px",
    padding: "12px 32px",
    fontSize: "15px",
    fontWeight: "600",
  },
  skipText: {
    textAlign: "center",
    marginTop: "16px",
    marginBottom: "0",
  },
  skipLink: {
    color: "#888",
    fontSize: "13px",
    textDecoration: "underline",
  },
  successAnimation: {
    fontSize: "72px",
    marginBottom: "16px",
  },
  thankYouTitle: {
    color: "#333",
    fontWeight: "700",
    fontSize: "26px",
    marginBottom: "8px",
  },
  thankYouSubtitle: {
    color: "#666",
    fontSize: "15px",
    marginBottom: "20px",
  },
  summaryContainer: {
    background: "#f8f9fa",
    borderRadius: "12px",
    padding: "16px",
    marginTop: "8px",
  },
  summaryItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 12px",
    borderBottom: "1px solid #e8e8e8",
  },
  summaryIcon: {
    fontSize: "18px",
    marginRight: "8px",
  },
  summaryLabel: {
    flex: 1,
    textAlign: "left",
    color: "#555",
    fontSize: "14px",
  },
};

export default RateDriverSection;
